import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Cropper from 'react-easy-crop';
import {Area} from 'react-easy-crop/types';
import {
  Box,
  useTheme,
  Typography,
  Slider,
  Tooltip,
  Grid,
  Button,
} from '@mui/material';
import {Add, Remove} from '@mui/icons-material';
import {feedback} from 'shared/services';

import getCroppedImg from './cropImage';

interface IUploadImageProps {
  imagePreview: string;
  setImage(value: string): void;
  onClose(): void;
  id: string;
  width: number;
  height: number;
}

export const UploadImage: React.FC<IUploadImageProps> = ({
  imagePreview,
  setImage,
  onClose,
  id,
  width,
  height,
}) => {
  const [crop, setCrop] = useState({x: 0, y: 0});
  const [zoom, setZoom] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(imagePreview);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const theme = useTheme();

  useEffect(() => {
    croppedImage && setImage(croppedImage);
  }, [croppedImage, setImage]);

  const onCropComplete = useCallback((_, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  function onZoomChange(zoom: number | number[]) {
    if (!Array.isArray(zoom)) {
      setZoom(zoom);
    }
  }

  const saveCroppedImage = useCallback(async () => {
    if (croppedAreaPixels != null && previewUrl) {
      try {
        const cropImg = await getCroppedImg(previewUrl, croppedAreaPixels);
        setCroppedImage(cropImg);

        onClose();
      } catch (error: any) {
        feedback(`Formato do arquivo ${error.message} não suportado.`, 'error');
      }
    } else {
      feedback(
        'Escolha uma imagem antes de adicioná-la ao questionário.',
        'warning',
      );
    }
  }, [croppedAreaPixels, onClose, previewUrl]);

  const dropImage = useCallback((event: DragEvent<HTMLInputElement>) => {
    event.preventDefault();

    const imageDropped = event.dataTransfer.items[0];
    const supportedFileTypes = ['image/png', 'image/jpg', 'image/jpeg'];

    if (supportedFileTypes.includes(imageDropped.type)) {
      if (imageDropped.getAsFile()) {
        setPreviewUrl(URL.createObjectURL(imageDropped.getAsFile() as Blob));
      }
    } else {
      feedback(
        `Formato do arquivo ${imageDropped.type} não suportado.`,
        'error',
      );
    }
  }, []);

  const imageChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setPreviewUrl(URL.createObjectURL(file));
    }
    event.target.value = '';
  }, []);

  return (
    <>
      <input
        onChange={imageChange}
        accept="image/*"
        id={id}
        type="file"
        hidden
      />
      {previewUrl ? (
        <>
          <Box position="relative" width="100%" height="300px">
            <Cropper
              image={previewUrl}
              crop={crop}
              zoom={zoom}
              zoomWithScroll={false}
              aspect={1}
              cropSize={{
                width: width,
                height: height,
              }}
              restrictPosition={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
            />
          </Box>
          <Box p={1}>
            <Typography align="center" variant="body2" component={Box}>
              Arraste para posicionar
            </Typography>
          </Box>
          <Box p={1}>
            <Box display="flex" justifyContent="center">
              <Box maxWidth="500px" width="100%">
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Remove />
                  </Grid>
                  <Grid item xs>
                    <Slider
                      defaultValue={1}
                      aria-labelledby="discrete-slider"
                      step={0.1}
                      min={1}
                      max={5}
                      onChange={(e, zoom) => onZoomChange(zoom)}
                    />
                  </Grid>
                  <Grid item>
                    <Add />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <label htmlFor={id}>
          <Tooltip title={`Dimensão recomendada: ${width}px x ${height}px`}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="400px"
              bgcolor="#00000041"
              borderColor={theme.palette.primary.main}
              style={{
                cursor: 'pointer',
              }}
              onDrop={dropImage}
              onDragOver={(e) => e.preventDefault()}>
              <Typography
                variant="subtitle1"
                style={{
                  userSelect: 'none',
                  color: theme.palette.primary.contrastText,
                }}>
                Arraste e solte uma imagem ou clique para escolher uma imagem
              </Typography>
            </Box>
          </Tooltip>
        </label>
      )}
      <Box display="flex" p={3} justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={saveCroppedImage} variant="outlined">
          Adicionar
        </Button>
      </Box>
    </>
  );
};
